import { useCallback, useEffect, useRef, useState } from 'react';
import type { FichaPago, MotivoReembolso, Reembolso, VersionPolitica } from '../../../api/ops/pagos';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Baldosas } from '../../../componentes/Baldosas';
import { CampoImporte } from '../../../componentes/CampoImporte';
import { Campo } from '../../../componentes/Campo';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { aCentimos, dinero, errorDeImporte } from '../../../logica/dinero';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError } from '../../../logica/estados';
import { diaMes, diaYHora } from '../../../logica/fechasCortas';
import { enlaceDeReferencia } from '../../../logica/navegacion';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { Copiar } from '../cuenta/Cuenta';
import { CONCEPTO, ESTADO_PAGO, FORMA_PAGO, MOTIVOS_REEMBOLSO, ORIGEN_REEMBOLSO, PERMISOS, textoDeError, tipoCobrable } from '../textos';

/**
 * Ficha de un pago (4.4) en RV Panel, Resumen · Movimientos, con:
 * - 4.5: un reembolso hecho desde el panel de Stripe sin decidir: «Cancelarla» (avisa al cliente: ventana, sin
 *   «Deshacer») o «Dejarla como está» (5 s de «Deshacer», G159), con `decideStripeDashboardRefund` (G285). Tras
 *   «Cancelarla», la cancelación la hace el módulo dueño al oír el evento (G386; nunca devuelve más de lo que queda).
 * - Reembolsar todo lo que queda (`refundable_amount` de la API) o una parte, con motivo y ventana «No se puede deshacer»;
 *   en los del local, cómo se devolvió (`external_method`). Anular un registro del local con motivo.
 * - Un cargo de no-show o de cancelación tardía rechazado por el banco: «Reintentar el cobro» sobre ESE pago, «Intentos N
 *   de 3» (G282, B5). Ni cobrar el no-show ni la cancelación tardía: los pide su módulo.
 * - Estados que también se ven: reembolso fallido, disputa (y su alerta temprana) y, con un acceso de plataforma abierto,
 *   sin reembolsar ni cobrar (D-059).
 */
interface Props extends PropsPantalla {
    id: string | null;
    nombres: Readonly<Record<string, string>>;
    onCerrar: () => void;
    onCambiado: () => void;
}

const INTENTOS_MAXIMOS = 3;

export function FichaDePago(props: Props) {
    const { ops, negocio, movil, id, nombres, onCerrar, onCambiado, soloLectura, destinos = [], navegar, ruta, accesoPlataforma = false } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [pago, setPago] = useState<FichaPago | null>(null);
    const [versionLeida, setVersion] = useState<{ pago: string; v: VersionPolitica; nombre: string } | null>(null);
    const [pestana, setPestana] = useState<'resumen' | 'movimientos'>('resumen');
    const [intento, setIntento] = useState(0);
    const [modo, setModo] = useState<'ficha' | 'reembolsar'>('ficha');
    const [ventana, setVentana] = useState<'reembolsar' | 'anular' | 'reintentar' | 'cancelarla' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    // Reembolsar
    const [todo, setTodo] = useState(true);
    const [parte, setParte] = useState('');
    const [motivo, setMotivo] = useState<MotivoReembolso | ''>('');
    const [nota, setNota] = useState('');
    const [conNota, setConNota] = useState(false);
    const [devueltoCon, setDevueltoCon] = useState<'cash' | 'card_terminal' | null>(null);
    const [intentado, setIntentado] = useState(false);
    // Anular
    const [motivoAnular, setMotivoAnular] = useState('');
    // 4.5
    const [decision, setDecision] = useState<'cancelled' | 'kept' | null>(null);

    // Otro pago: se empieza por su resumen.
    const [idAntes, setIdAntes] = useState(id);
    if (id !== idAntes) {
        setIdAntes(id);
        setModo('ficha');
        setPestana('resumen');
        setDecision(null);
    }

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    // `onCerrar` llega nuevo en cada pintado: se usa el último sin volver a pedir el pago.
    const cerrar = useRef(onCerrar);
    useEffect(() => {
        cerrar.current = onCerrar;
    });

    useEffect(() => {
        if (id === null) {
            return;
        }
        let vivo = true;
        ops.getPayment(negocio, id)
            .then((p) => {
                if (!vivo) {
                    return;
                }
                setPago(p);
                const vId = p.policy_version_ids?.[0];
                if (vId !== undefined) {
                    ops.getPaymentPolicyVersion(negocio, vId)
                        .then(async (v) => {
                            const nombre = await ops.getPaymentPolicy(negocio, v.policy_id).then((r) => r.datos.name).catch(() => '');
                            if (vivo) {
                                setVersion({ pago: p.id, v, nombre });
                            }
                        })
                        .catch(() => undefined);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    cerrar.current();
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido abrir el pago. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, intento, avisar]);

    const hecho = (texto: string) => {
        setOcupado(false);
        setVentana(null);
        setModo('ficha');
        avisar({ tipo: 'ok', texto });
        recargar();
        onCambiado();
    };
    const fallo = (e: unknown, porDefecto: string) => {
        setOcupado(false);
        setVentana(null);
        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, porDefecto) });
    };

    // Lo leído solo vale para el pago abierto (al cambiar de pago, «Cargando…» hasta que llega el nuevo).
    const p = pago !== null && pago.id === id ? pago : null;
    const version = versionLeida !== null && versionLeida.pago === id ? versionLeida : null;
    const nombre = p?.customer_id == null ? '' : nombres[p.customer_id] ?? '';
    const externo = p?.origin === 'external';
    const sinPlataforma = !accesoPlataforma;
    const puedeReembolsar = p !== null && sinPlataforma && permisos.puede(PERMISOS.reembolsar) && p.refundable_amount !== undefined && p.refundable_amount.amount > 0 && p.status !== 'disputed';
    const puedeAnular = p !== null && externo && p.status === 'registered' && (p.refunded_amount?.amount ?? 0) === 0 && permisos.puede(PERMISOS.anular);
    const esCargo = p !== null && (p.concept === 'no_show_fee' || p.concept === 'late_cancellation_fee');
    const intentos = p?.attempts ?? [];
    const puedeReintentar = p !== null && esCargo && p.status === 'rejected' && intentos.length < INTENTOS_MAXIMOS && sinPlataforma && permisos.puede(PERMISOS.cobrar) && !soloLectura;
    const porDecidir: Reembolso | undefined = p?.refunds?.find((r) => r.origin === 'stripe_dashboard' && r.decision == null && r.status !== 'failed');
    const puedeDecidir = permisos.puede(PERMISOS.decidir);
    const fallidos = (p?.refunds ?? []).filter((r) => r.status === 'failed');

    // ---- Reembolsar ----
    const importeParte = aCentimos(parte);
    const errParte = intentado && !todo ? errorDeImporte(parte, true) : null;
    const devuelves = p === null ? null : todo ? (p.refundable_amount ?? null) : importeParte === null ? null : { amount: importeParte, currency: p.amount.currency };
    const abrirReembolso = () => {
        setTodo(true);
        setParte('');
        setMotivo('');
        setNota('');
        setConNota(false);
        setDevueltoCon(null);
        setIntentado(false);
        setModo('reembolsar');
    };
    const pedirConfirmacion = () => {
        setIntentado(true);
        if (motivo === '' || (!todo && errorDeImporte(parte, true) !== null) || (externo && devueltoCon === null)) {
            return;
        }
        setVentana('reembolsar');
    };
    const reembolsar = () => {
        if (p === null || motivo === '') {
            return;
        }
        setOcupado(true);
        ops.refundPayment(negocio, p.id, {
            reason: motivo,
            ...(todo || importeParte === null ? {} : { amount: { amount: importeParte, currency: p.amount.currency } }),
            ...(nota.trim() === '' ? {} : { note: nota.trim() }),
            ...(externo && devueltoCon !== null ? { external_method: devueltoCon } : {}),
        })
            .then((r) => {
                hecho(r.status === 'succeeded' ? `Devueltos ${dinero(r.amount)}.` : `Devolución de ${dinero(r.amount)} en marcha: el cliente la verá en su banco en unos días.`);
            })
            .catch((e: unknown) => {
                fallo(e, e instanceof ErrorApi && e.estado === 409 ? 'Ahora no se puede reembolsar este pago.' : 'No he podido hacer el reembolso. Prueba de nuevo.');
            });
    };

    // ---- 4.5 ----
    const aplicarDecision = () => {
        if (porDecidir === undefined || decision === null) {
            return;
        }
        if (decision === 'cancelled') {
            setVentana('cancelarla');
            return;
        }
        envio.programar({
            texto: 'Reembolso de Stripe resuelto: la reserva sigue como está.',
            donde: porDecidir.id,
            enviar: () => {
                ops.decideStripeDashboardRefund(negocio, porDecidir.id, 'kept')
                    .then(() => {
                        recargar();
                        onCambiado();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido guardar la decisión. Prueba de nuevo.') });
                    });
            },
        });
        setDecision(null);
    };
    const cancelarla = () => {
        if (porDecidir === undefined || p === null) {
            return;
        }
        setOcupado(true);
        ops.decideStripeDashboardRefund(negocio, porDecidir.id, 'cancelled')
            .then(() => {
                // G386: la decisión publica `ReembolsoExternoDecidido` y el módulo dueño cancela por su cuenta.
                setDecision(null);
                hecho('Reembolso de Stripe resuelto: la cancelamos y la plaza queda libre en unos segundos.');
            })
            .catch((e: unknown) => {
                fallo(e, 'No he podido guardar la decisión. Prueba de nuevo.');
            });
    };

    // ---- Cabecera del panel ----
    const accion = modo === 'reembolsar' ? (devuelves === null ? 'Reembolsar' : `Reembolsar ${dinero(devuelves)}`) : puedeReembolsar ? 'Reembolsar' : '';
    const [estadoTexto, estadoChip] = p === null ? ['', 'neutral' as const] : ESTADO_PAGO[p.status];

    const cuerpo = (() => {
        if (p === null) {
            return <EstadoCargando seccion="el pago" />;
        }
        if (modo === 'reembolsar') {
            return (
                <div className="rv-ca-cuerpo">
                    <div className="rv-ca-bloque">
                        <p className="rv-ca-ceja">Cuánto devuelves</p>
                        <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                            <OpcionRadio elegida={todo} onElegir={() => { setTodo(true); }} titulo="Todo lo que queda" texto={dinero(p.refundable_amount)} />
                            <OpcionRadio elegida={!todo} onElegir={() => { setTodo(false); }} titulo="Una parte" texto={`Hasta ${dinero(p.refundable_amount)}`} />
                        </div>
                        {!todo && <CampoImporte etiqueta="Cuánto devuelves" moneda={p.amount.currency} valor={parte} onChange={setParte} error={errParte} />}
                    </div>
                    <Desplegable
                        etiqueta="Motivo"
                        valor={motivo}
                        opciones={[{ valor: '', texto: 'Elige el motivo' }, ...MOTIVOS_REEMBOLSO.map(([valor, texto]) => ({ valor, texto }))]}
                        onChange={(v) => { setMotivo(v as MotivoReembolso | ''); }}
                        error={intentado && motivo === '' ? 'Elige el motivo.' : null}
                    />
                    {externo && (
                        <div className="rv-ca-bloque">
                            <p className="rv-ca-ceja">Cómo lo devuelves</p>
                            <Baldosas etiqueta="Cómo lo devuelves" opciones={[{ valor: 'cash', texto: 'Efectivo', icono: 'banknote' }, { valor: 'card_terminal', texto: 'Datáfono', icono: 'credit-card' }]} valor={devueltoCon} onCambio={setDevueltoCon} />
                            {intentado && devueltoCon === null && <span className="rv-campo__error" role="alert"><Icono nombre="circle-alert" /><span>Elige cómo lo devuelves.</span></span>}
                        </div>
                    )}
                    {conNota ? (
                        <Campo etiqueta="Nota" valor={nota} onChange={setNota} filas={3} max={500} ayuda="Solo la ve tu equipo." />
                    ) : (
                        <button type="button" className="rv-pg-enlace" onClick={() => { setConNota(true); }}><Icono nombre="plus" /><span>Añadir una nota</span></button>
                    )}
                    <div className="rv-ca-bloque">
                        <Dato k="Cobrado" v={dinero(p.amount)} />
                        <Dato k="Ya devuelto" v={dinero(p.refunded_amount ?? { amount: 0, currency: p.amount.currency })} />
                        <Dato k="Devuelves ahora" v={devuelves === null ? '—' : dinero(devuelves)} />
                        {!externo && <Dato k="Comisión de plataforma" v="Según el motivo" />}
                    </div>
                    <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>{externo ? 'Se anota como devuelto en el local.' : 'Stripe lo devuelve a la tarjeta con la que pagó; el cliente lo ve en su banco en unos días.'}</span></p>
                </div>
            );
        }
        if (pestana === 'movimientos') {
            return <Movimientos pago={p} />;
        }
        const [tipo, icono] = tipoCobrable(p.payable.type);
        const enlaceCliente = p.customer_id == null ? null : enlaceDeReferencia(destinos, 'cliente', p.customer_id);
        const enlaceCobrable = enlaceDeReferencia(destinos, p.payable.type, p.payable.id);
        const referencia = p.stripe_references?.payment_intent_id ?? p.stripe_references?.charge_id ?? p.stripe_references?.checkout_session_id ?? null;
        return (
            <div className="rv-ca-cuerpo">
                {porDecidir !== undefined && (
                    <div className="rv-pg-decidir">
                        <div className="rv-ca-caja__fila">
                            <span className="rv-pg-baldosa rv-pg-baldosa--amarilla"><Icono nombre="rotate-ccw" /></span>
                            <span className="rv-ca-fila__texto">
                                <strong>Reembolsado desde el panel de Stripe</strong>
                                <span>{`Alguien devolvió ${dinero(porDecidir.amount)} el ${diaMes(porDecidir.completed_at ?? porDecidir.created_at)} directamente en Stripe, fuera de Reservaria. ${mayus(tipo)} sigue en pie. ¿Qué hacemos con ella?`}</span>
                            </span>
                        </div>
                        {puedeDecidir ? (
                            <>
                                <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                                    <OpcionRadio elegida={decision === 'cancelled'} onElegir={() => { setDecision('cancelled'); }} titulo="Cancelarla" texto="Se cancela sin volver a reembolsar y se libera la plaza." />
                                    <OpcionRadio elegida={decision === 'kept'} onElegir={() => { setDecision('kept'); }} titulo="Dejarla como está" texto="Por ejemplo, si fue una devolución pactada." />
                                </div>
                                <span>
                                    <button type="button" className="rv-b rv-b-pri" disabled={decision === null} onClick={aplicarDecision}><Icono nombre="check" /><span>Aplicar</span></button>
                                </span>
                            </>
                        ) : (
                            <span className="rv-ca-nota"><Icono nombre="eye" /><span>Qué pasa con la reserva lo decide el propietario o un administrador.</span></span>
                        )}
                    </div>
                )}
                {envio.pendiente !== null && (
                    <div className="rv-ca-hecho" role="status">
                        <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                        <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                    </div>
                )}
                {fallidos.map((r) => (
                    <div key={r.id} className="rv-ca-caja rv-ca-caja--error" role="alert">
                        <div className="rv-ca-caja__fila"><Icono nombre="circle-alert" /><span>{`Stripe no pudo devolver ${dinero(r.amount)}${r.failure_reason == null ? '' : ` (${r.failure_reason})`}. Vuelve a intentarlo o devuélvelo por tu cuenta y anótalo.`}</span></div>
                    </div>
                ))}
                {p.dispute != null && (
                    <div className={`rv-ca-caja ${p.dispute.early_warning === true ? 'rv-ca-caja--aviso' : 'rv-ca-caja--error'}`}>
                        <div className="rv-ca-caja__fila">
                            <Icono nombre="scale" />
                            <span>{p.dispute.early_warning === true ? 'Aviso temprano de disputa: el banco del cliente lo está revisando. Aún puedes devolverlo y evitar la disputa.' : 'Este pago está en disputa: no se puede reembolsar hasta que se resuelva.'}</span>
                        </div>
                        <span>
                            <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`${ruta}?apartado=disputas&disputa=${encodeURIComponent(p.dispute?.id ?? '')}`); }}><span>Ver la disputa</span><Icono nombre="chevron-right" /></button>
                        </span>
                    </div>
                )}
                {esCargo && (p.status === 'rejected' || intentos.length > 0) && (
                    <div className={`rv-ca-caja${p.status === 'rejected' ? ' rv-ca-caja--error' : ''}`}>
                        <div className="rv-ca-caja__fila">
                            <Icono nombre={p.status === 'rejected' ? 'circle-alert' : 'refresh-cw'} />
                            <span>
                                {p.status === 'rejected' ? `Su banco rechazó el cobro${intentos.at(-1)?.decline_label == null ? '' : `: ${intentos.at(-1)?.decline_label ?? ''}`}. ` : ''}
                                {`Intentos ${String(intentos.length)} de ${String(INTENTOS_MAXIMOS)}.`}
                            </span>
                        </div>
                        {puedeReintentar && (
                            <span>
                                <button type="button" className="rv-b rv-b-sec" onClick={() => { setVentana('reintentar'); }}><Icono nombre="refresh-cw" /><span>Reintentar el cobro</span></button>
                            </span>
                        )}
                    </div>
                )}
                <div className="rv-ca-bloque">
                    <Dato k="De qué" icono={icono} v={[tipo, p.description ?? ''].filter((x) => x !== '').join(' · ')} boton={enlaceCobrable === null ? undefined : { texto: `Abrir ${tipo.toLocaleLowerCase('es')}`, accion: () => { navegar(enlaceCobrable.ruta); } }} />
                    {p.customer_id != null && <Dato k="Cliente" icono="user" v={nombre === '' ? 'Cliente' : nombre} boton={enlaceCliente === null ? undefined : { texto: 'Ver ficha', accion: () => { navegar(enlaceCliente.ruta); } }} />}
                    <Dato k="Forma de pago" icono="credit-card" v={[p.method == null ? '' : FORMA_PAGO[p.method][0], externo ? 'en el local' : 'online, con Stripe'].filter((x) => x !== '').join(' · ')} />
                    {version !== null && <Dato k="Política aplicada" icono="file-text" v={[version.nombre, `versión ${String(version.v.number)}`].filter((x) => x !== '').join(' · ')} sub="La que aceptó al pagar; no cambia aunque edites la política." />}
                    {p.booking_fee !== undefined && p.booking_fee.amount > 0 && <Dato k="Gastos de gestión" icono="receipt" v={dinero(p.booking_fee)} sub="Incluidos en el importe que pagó." />}
                    {p.refunded_amount !== undefined && p.refunded_amount.amount > 0 && <Dato k="Devuelto" icono="undo-2" v={dinero(p.refunded_amount)} />}
                    {p.refundable_amount !== undefined && p.refundable_amount.amount > 0 && <Dato k="Queda por devolver" icono="wallet" v={dinero(p.refundable_amount)} />}
                    {!externo && p.stripe_fee !== undefined && (
                        <Dato k="Comisiones" icono="percent" v={`${dinero(p.stripe_fee)} de Stripe · ${dinero(p.platform_fee?.amount ?? { amount: 0, currency: p.amount.currency })} de plataforma`} />
                    )}
                    {p.status === 'voided' && p.void_reason != null && <Dato k="Anulado" icono="ban" v={p.void_reason} />}
                    {referencia !== null && <Dato k="Referencia en Stripe" icono="hash" v={referencia} copiar />}
                </div>
                <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="lock" /><span>Los datos de la tarjeta los guarda Stripe: nunca se ven en Reservaria.</span></p>
                {accesoPlataforma && <p className="rv-ca-nota"><Icono nombre="eye" /><span>Con un acceso de plataforma abierto no se reembolsa ni se cobra.</span></p>}
                {puedeAnular && (
                    <span>
                        <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={() => { setMotivoAnular(''); setIntentado(false); setVentana('anular'); }}><Icono nombre="ban" /><span>Anular el registro</span></button>
                    </span>
                )}
            </div>
        );
    })();

    return (
        <>
            <PanelLateral
                abierto={id !== null}
                onCerrar={modo === 'reembolsar' ? () => { setModo('ficha'); } : onCerrar}
                movil={movil}
                tamano={modo === 'reembolsar' ? 'formulario' : 'ficha'}
                titulo={p === null ? 'Pago' : modo === 'reembolsar' ? (nombre === '' ? 'Devolver el pago' : `Devolver a ${nombre}`) : p.concept === 'guarantee' && p.amount.amount === 0 ? 'Sin cargo' : dinero(p.amount)}
                eyebrow={p === null ? undefined : modo === 'reembolsar' ? 'Reembolsar' : CONCEPTO[p.concept]}
                subtitulo={p === null ? undefined : modo === 'reembolsar' ? `${CONCEPTO[p.concept]} · cobrado ${dinero(p.amount)}` : [nombre, diaYHora(p.created_at)].filter((x) => x !== '').join(' · ')}
                chip={p === null || modo === 'reembolsar' ? undefined : estadoTexto}
                chipEstado={estadoChip}
                icono={modo === 'reembolsar' ? 'rotate-ccw' : 'receipt'}
                accion={accion}
                accionIcono="rotate-ccw"
                cerrar={modo === 'reembolsar' ? 'Cancelar' : 'Cerrar'}
                onAccion={modo === 'reembolsar' ? pedirConfirmacion : abrirReembolso}
            >
                {p !== null && modo === 'ficha' && (
                    <div className="rv-ca-botonera" role="tablist" aria-label="Partes del pago">
                        {(
                            [
                                ['resumen', 'Resumen', 'receipt'],
                                ['movimientos', 'Movimientos', 'history'],
                            ] as const
                        ).map(([k, t, ic]) => (
                            <button key={k} type="button" role="tab" aria-selected={pestana === k} className="rv-ca-botonera__b" onClick={() => { setPestana(k); }}>
                                <Icono nombre={ic} />
                                <span>{t}</span>
                            </button>
                        ))}
                    </div>
                )}
                {cuerpo}
            </PanelLateral>
            <Ventana
                abierta={ventana === 'reembolsar'}
                titulo={devuelves === null ? 'Reembolsar' : `¿Devolver ${dinero(devuelves)}${nombre === '' ? '' : ` a ${nombre}`}?`}
                texto={externo ? 'Se anota como devuelto en el local. No se puede deshacer.' : 'Stripe lo devuelve a la tarjeta con la que pagó. No se puede deshacer.'}
                accion={devuelves === null ? 'Reembolsar' : `Reembolsar ${dinero(devuelves)}`}
                accionIcono="rotate-ccw"
                ocupado={ocupado}
                ocupadoTexto="Reembolsando…"
                movil={movil}
                onAccion={reembolsar}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'anular'}
                titulo="¿Anular este registro?"
                texto="No se borra: queda anulado con su motivo y deja de contar como cobrado."
                accion="Anular"
                accionIcono="ban"
                ocupado={ocupado}
                ocupadoTexto="Anulando…"
                movil={movil}
                onAccion={() => {
                    setIntentado(true);
                    if (p === null || motivoAnular.trim() === '') {
                        return;
                    }
                    setOcupado(true);
                    ops.voidExternalPayment(negocio, p.id, motivoAnular.trim())
                        .then(() => { hecho('Registro anulado.'); })
                        .catch((e: unknown) => { fallo(e, 'No he podido anularlo. Prueba de nuevo.'); });
                }}
                onCerrar={() => { setVentana(null); }}
            >
                <Campo etiqueta="Motivo" valor={motivoAnular} onChange={setMotivoAnular} filas={3} max={500} error={intentado && motivoAnular.trim() === '' ? 'Escribe el motivo.' : null} ayuda="Queda en Actividad." />
            </Ventana>
            <Ventana
                abierta={ventana === 'reintentar'}
                titulo="¿Reintentar el cobro?"
                texto={p === null ? '' : `Se vuelve a cobrar ${dinero(p.amount)} con la tarjeta de garantía. Es el intento ${String(intentos.length + 1)} de ${String(INTENTOS_MAXIMOS)}. No se puede deshacer.`}
                accion="Reintentar el cobro"
                accionIcono="refresh-cw"
                ocupado={ocupado}
                ocupadoTexto="Cobrando…"
                movil={movil}
                onAccion={() => {
                    if (p === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.retryOffSessionCharge(negocio, p.id)
                        .then((r) => { hecho(r.status === 'completed' ? `Cobrados ${dinero(r.amount)}.` : 'Su banco lo ha vuelto a rechazar.'); })
                        .catch((e: unknown) => { fallo(e, 'No he podido reintentar el cobro. Prueba de nuevo.'); });
                }}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'cancelarla'}
                titulo="¿Cancelarla?"
                texto="Se cancela sin volver a reembolsar lo que ya se devolvió en Stripe, se libera la plaza y se avisa al cliente. No se puede deshacer."
                accion="Cancelarla"
                accionIcono="calendar-x"
                ocupado={ocupado}
                ocupadoTexto="Cancelando…"
                movil={movil}
                onAccion={cancelarla}
                onCerrar={() => { setVentana(null); }}
            />
        </>
    );
}

const mayus = (t: string) => `La ${t.toLocaleLowerCase('es')}`;

function Dato({ k, v, sub, icono, boton, copiar = false }: { k: string; v: string; sub?: string; icono?: NombreIcono; boton?: { texto: string; accion: () => void }; copiar?: boolean }) {
    return (
        <div className="rv-ca-dato">
            <span className="rv-pg-dato-k">{icono !== undefined && <Icono nombre={icono} />}{k}</span>
            <span className="rv-pg-dato-v">
                <span className="rv-pg-dato-fila">
                    <strong>{v}</strong>
                    {boton !== undefined && <button type="button" className="rv-b rv-b-sec" onClick={boton.accion}><Icono nombre="arrow-right" /><span>{boton.texto}</span></button>}
                    {copiar && <Copiar texto={v} />}
                </span>
                {sub !== undefined && <small>{sub}</small>}
            </span>
        </div>
    );
}

interface FilaMovimiento {
    cuando: string;
    icono: NombreIcono;
    titulo: string;
    linea: string;
    importe: string;
}

function Movimientos({ pago }: { pago: FichaPago }) {
    const filas: FilaMovimiento[] = [
        ...(pago.attempts ?? []).map((a): FilaMovimiento => ({
            cuando: a.attempted_at,
            icono: a.result === 'succeeded' ? 'check' : a.result === 'declined' || a.result === 'error' ? 'circle-x' : 'clock',
            titulo: `Intento ${String(a.attempt_number)}`,
            linea: a.result === 'succeeded' ? 'Cobrado' : a.result === 'declined' ? (a.decline_label ?? 'Rechazado por el banco') : a.result === 'requires_action' ? 'El banco pide confirmar' : a.result === 'processing' ? 'En proceso' : 'Error',
            importe: '',
        })),
        ...(pago.refunds ?? []).map((r): FilaMovimiento => ({
            cuando: r.completed_at ?? r.created_at,
            icono: r.status === 'failed' ? 'circle-x' : 'rotate-ccw',
            titulo: r.status === 'failed' ? 'Reembolso fallido' : r.status === 'pending' ? 'Reembolso en marcha' : 'Reembolso',
            linea: [ORIGEN_REEMBOLSO[r.origin], MOTIVOS_REEMBOLSO.find(([m]) => m === r.reason)?.[1] ?? ''].filter((x) => x !== '').join(' · '),
            importe: `−${dinero(r.amount)}`,
        })),
        ...(pago.dispute == null ? [] : [{ cuando: pago.dispute.opened_at, icono: 'scale' as NombreIcono, titulo: 'Disputa abierta', linea: pago.dispute.bank_reason_label ?? pago.dispute.bank_reason, importe: dinero(pago.dispute.amount) }]),
        { cuando: pago.created_at, icono: 'receipt' as NombreIcono, titulo: 'Creado', linea: CONCEPTO[pago.concept], importe: dinero(pago.amount) },
    ].sort((a, b) => b.cuando.localeCompare(a.cuando));
    return (
        <div className="rv-ca-bloque" role="list" aria-label="Movimientos del pago">
            {filas.map((f, i) => (
                <div key={`${f.cuando}${String(i)}`} role="listitem" className="rv-pg-movimiento">
                    <span className="rv-ca-baldosa"><Icono nombre={f.icono} /></span>
                    <span className="rv-ca-fila__texto"><strong>{f.titulo}</strong><span>{[f.linea, diaYHora(f.cuando)].filter((x) => x !== '').join(' · ')}</span></span>
                    {f.importe !== '' && <strong className="rv-pg-importe">{f.importe}</strong>}
                </div>
            ))}
        </div>
    );
}
